import { createContext, useContext, useEffect, useRef, useState } from 'react'

/**
 * What the filing rail shows beyond the phase it can read from the URL.
 *
 * The rail lives in the app shell, in the sidebar's place, so the page that knows which
 * question somebody is on is a child of it rather than its parent. This is the channel back
 * up. Only the intake page uses it — every later phase is one page, and FilingRail reads that
 * from the route — so most filing pages need no code at all.
 */
const FilingRailContext = createContext(null)

export function FilingRailProvider({ children }) {
  const [rail, setRail] = useState(null)
  return <FilingRailContext.Provider value={{ rail, setRail }}>{children}</FilingRailContext.Provider>
}

export function useFilingRailState() {
  return useContext(FilingRailContext)?.rail ?? null
}

/**
 * Publish the intake page's position to the rail.
 *
 * @param {object} config
 * @param {number} [config.phase]     override the phase read from the URL
 * @param {Array<{id: string, label: string}>} [config.substeps]
 * @param {number} [config.active]    index of the substep on screen
 * @param {number} [config.furthest]  furthest substep reached; it and everything before it
 *                                    can be clicked
 * @param {(index: number) => void} [config.onSelect]
 */
export function useFilingRail({ phase, substeps, active, furthest, onSelect }) {
  const ctx = useContext(FilingRailContext)
  // The handler changes identity every render; a ref keeps the effect keyed on data only.
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect
  const substepKey = substeps ? substeps.map((s) => s.id).join('|') : ''

  useEffect(() => {
    if (!ctx) return
    ctx.setRail({
      phase,
      substeps,
      active,
      furthest,
      onSelect: (i) => onSelectRef.current?.(i),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, substepKey, active, furthest])

  useEffect(() => () => ctx?.setRail(null), [])  // eslint-disable-line react-hooks/exhaustive-deps
}
